import React, { useState } from 'react';
import {
  X,
  Anchor,
  Users,
  Bed,
  Calendar,
  MapPin,
  Sparkles,
  Gauge,
  ShieldCheck,
  CheckCircle2,
  ChevronRight,
  Send,
  Ship,
  Layers,
} from 'lucide-react';
import { YachtItem } from '../../types';

interface PublicYachtDetailModalProps {
  yacht: YachtItem | null;
  onClose: () => void;
  onBookYacht: (yacht: YachtItem) => void;
  primaryColor?: string;
}

export const PublicYachtDetailModal: React.FC<PublicYachtDetailModalProps> = ({
  yacht,
  onClose,
  onBookYacht,
  primaryColor = '#0284C7',
}) => {
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  if (!yacht) return null;

  const images = [
    yacht.coverImage || yacht.imageUrl,
    ...(yacht.gallery || yacht.galleryImages || []),
  ].filter(Boolean) as string[];

  const currentImage = images[activeImageIndex] || yacht.coverImage;

  const currencySymbol = yacht.currency === 'TRY' ? '₺' : yacht.currency === 'USD' ? '$' : '€';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 md:p-6 bg-slate-950/80 backdrop-blur-sm animate-in fade-in overflow-y-auto select-none">
      <div className="bg-white rounded-3xl shadow-2xl border border-slate-200/80 w-full max-w-4xl overflow-hidden flex flex-col my-auto max-h-[92vh] relative animate-in zoom-in-95">
        {/* Header Bar with close */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/70 shrink-0">
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-sky-100 text-sky-800 flex items-center gap-1">
              <Ship className="w-3.5 h-3.5" />
              {yacht.type || 'Lüks Yat'}
            </span>
            <span
              className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                yacht.status === 'AVAILABLE' || !yacht.status
                  ? 'bg-emerald-100 text-emerald-800'
                  : yacht.status === 'BOOKED'
                  ? 'bg-amber-100 text-amber-800'
                  : 'bg-slate-200 text-slate-700'
              }`}
            >
              {yacht.status === 'AVAILABLE' || !yacht.status
                ? 'Müsait'
                : yacht.status === 'BOOKED'
                ? 'Kirada / Dolu'
                : 'Bakımda'}
            </span>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 transition cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto flex-1 p-6 space-y-6">
          {/* Main Visual Showcase */}
          <div className="space-y-3">
            <div className="relative h-64 sm:h-80 md:h-96 rounded-2xl overflow-hidden bg-slate-100 shadow-inner group">
              <img
                src={currentImage}
                alt={yacht.name}
                className="w-full h-full object-cover transition duration-300 group-hover:scale-105"
                referrerPolicy="no-referrer"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent pointer-events-none" />

              <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
                <div>
                  <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight drop-shadow-md">
                    {yacht.name}
                  </h2>
                  <div className="flex items-center gap-3 text-white/90 text-xs sm:text-sm font-medium mt-1">
                    {(yacht.portLocation || yacht.basePort || yacht.location) && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5 text-sky-300" />
                        {yacht.portLocation || yacht.basePort || yacht.location}
                      </span>
                    )}
                    {yacht.lengthMeters && (
                      <span className="flex items-center gap-1">
                        <Anchor className="w-3.5 h-3.5 text-sky-300" />
                        {yacht.lengthMeters} Metre ({yacht.buildYear || yacht.year} Yapım)
                      </span>
                    )}
                  </div>
                </div>

                <div className="bg-white/95 backdrop-blur-md px-4 py-2.5 rounded-2xl shadow-lg text-right shrink-0 border border-white/60">
                  <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                    Günlük Kiralama
                  </div>
                  <div className="text-xl sm:text-2xl font-black text-slate-900 leading-none mt-0.5">
                    {currencySymbol}{yacht.pricePerDay.toLocaleString('tr-TR')}
                  </div>
                  {yacht.pricePerWeek ? (
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      Haftalık {currencySymbol}{yacht.pricePerWeek.toLocaleString('tr-TR')}
                    </div>
                  ) : (
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      Haftalık {currencySymbol}{(yacht.pricePerDay * 7).toLocaleString('tr-TR')}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Gallery Thumbnails */}
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setActiveImageIndex(idx)}
                    className={`relative w-20 h-14 rounded-xl overflow-hidden shrink-0 border-2 transition ${
                      activeImageIndex === idx
                        ? 'border-sky-500 shadow-md scale-105'
                        : 'border-transparent opacity-70 hover:opacity-100'
                    }`}
                  >
                    <img
                      src={img}
                      alt={`Fotoğraf ${idx + 1}`}
                      className="w-full h-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Technical Specs Bento Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
            <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200/80 text-center">
              <Users className="w-5 h-5 mx-auto text-sky-600 mb-1" />
              <div className="text-[11px] font-bold text-slate-500">Kapasite</div>
              <div className="text-sm font-extrabold text-slate-900 mt-0.5">
                {yacht.capacity} Misafir
              </div>
            </div>

            <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200/80 text-center">
              <Bed className="w-5 h-5 mx-auto text-sky-600 mb-1" />
              <div className="text-[11px] font-bold text-slate-500">Kabin</div>
              <div className="text-sm font-extrabold text-slate-900 mt-0.5">
                {yacht.cabins} Kabin
              </div>
            </div>

            <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200/80 text-center">
              <Anchor className="w-5 h-5 mx-auto text-sky-600 mb-1" />
              <div className="text-[11px] font-bold text-slate-500">Boy / Uzunluk</div>
              <div className="text-sm font-extrabold text-slate-900 mt-0.5">
                {yacht.lengthMeters || yacht.length || '-'} m
              </div>
            </div>

            <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200/80 text-center">
              <Calendar className="w-5 h-5 mx-auto text-sky-600 mb-1" />
              <div className="text-[11px] font-bold text-slate-500">Yapım / Refit</div>
              <div className="text-sm font-extrabold text-slate-900 mt-0.5">
                {yacht.buildYear || yacht.year || '-'}
              </div>
            </div>

            <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200/80 text-center">
              <Users className="w-5 h-5 mx-auto text-sky-600 mb-1" />
              <div className="text-[11px] font-bold text-slate-500">Mürettebat</div>
              <div className="text-sm font-extrabold text-slate-900 mt-0.5">
                {yacht.crewCount || 3} Kişi
              </div>
            </div>

            <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200/80 text-center">
              <Gauge className="w-5 h-5 mx-auto text-sky-600 mb-1" />
              <div className="text-[11px] font-bold text-slate-500">Seyir Hızı</div>
              <div className="text-sm font-extrabold text-slate-900 mt-0.5">
                {yacht.cruisingSpeedKnots || 12} Knot
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <h3 className="text-base font-bold text-slate-900">Yat Hakkında</h3>
            <p className="text-xs sm:text-sm text-slate-600 leading-relaxed whitespace-pre-line">
              {yacht.description ||
                `${yacht.name}, Ege ve Akdeniz'in en güzel koylarında unutulmaz bir mavi tur deneyimi yaşatmak için donatılmıştır. Geniş güverte alanları, lüks yaşam alanları ve deneyimli mürettebatı ile hizmetinizdedir.`}
            </p>
          </div>

          {/* Amenities & Features */}
          {((yacht.features && yacht.features.length > 0) || (yacht.amenities && yacht.amenities.length > 0)) && (
            <div className="space-y-3">
              <h3 className="text-base font-bold text-slate-900">Donanım & Hizmetler</h3>
              <div className="flex flex-wrap gap-2">
                {[...(yacht.features || []), ...(yacht.amenities || [])].map((item, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-100 text-slate-700 text-xs font-medium border border-slate-200/70"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <span>{item}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Blue Cruise Guarantee */}
          <div className="p-4 rounded-2xl bg-sky-50/70 border border-sky-200/80 flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-sky-600 shrink-0 mt-0.5" />
            <div className="text-xs text-sky-900">
              <span className="font-bold block text-sky-950">Adiosoft Güvenceli Yat Kiralama Hizmeti</span>
              Tüm yatlarımız denizcilik mevzuatına uygun, tam sigortalı ve profesyonel lisanslı kaptan/mürettebat kadrosuna sahiptir.
              Rezervasyon talebiniz doğrudan yetkili acenteye iletilir.
            </div>
          </div>
        </div>

        {/* Footer Action Bar */}
        <div className="p-4 sm:p-5 border-t border-slate-100 bg-white flex flex-col sm:flex-row items-center justify-between gap-4 shrink-0">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Sparkles className="w-4 h-4 text-amber-500" />
            <span>Erken rezervasyonda özel rota avantajları ve takvim opsiyonu.</span>
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 sm:flex-none px-5 py-3 rounded-2xl bg-white hover:bg-slate-100 border border-slate-200 text-slate-700 font-bold text-xs transition cursor-pointer"
            >
              Kapat
            </button>
            <button
              type="button"
              onClick={() => {
                onClose();
                onBookYacht(yacht);
              }}
              style={{ backgroundColor: primaryColor }}
              className="flex-1 sm:flex-none px-6 py-3 rounded-2xl text-white font-extrabold text-xs sm:text-sm shadow-lg shadow-sky-500/25 hover:opacity-95 transition flex items-center justify-center gap-2 cursor-pointer"
            >
              <Send className="w-4 h-4" />
              <span>Bu Yatı Kirala / Teklif Al</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
